import { Scene, Skeleton, AnimationGroup, TransformNode, MeshBuilder, StandardMaterial, Color3, Vector3, AbstractMesh } from '@babylonjs/core';
import { CharacterLoader, CharacterAsset } from '../assets/CharacterLoader';
import { CharacterConfig, AnimationPhase } from '../config/characters';
import { isEmbeddedReference, ExtractionResult, ExtractedEntity } from '../assets/EntityAssetExtractor';
import { logger } from '../core/Logger';

/**
 * Cache for preloaded character assets to enable instant character switching
 */
export class CharacterAssetCache {
  private cache: Map<string, CharacterAsset> = new Map();
  private loader: CharacterLoader;

  constructor(scene: Scene) {
    this.loader = new CharacterLoader(scene);
  }

  /**
   * Preload a character's assets into the cache
   */
  async preload(config: CharacterConfig): Promise<void> {
    if (this.cache.has(config.id)) {
      logger.assets.debug(`Character '${config.id}' already cached`);
      return;
    }

    // Check if any attack uses embedded projectile references
    const hasEmbeddedProjectiles = config.combatAttacks 
      ? Object.values(config.combatAttacks).some(attack => 
          attack.projectile?.assetPath && isEmbeddedReference(attack.projectile.assetPath)
        )
      : false;

    // Check if any attack uses attachWeapon event (meaning embedded weapon entity)
    const hasEmbeddedWeapons = config.combatAttacks
      ? Object.values(config.combatAttacks).some(attack =>
          attack.events?.some(event => event.type === 'attachWeapon')
        )
      : false;

    const needsEntityExtraction = hasEmbeddedProjectiles || hasEmbeddedWeapons;
    
    logger.assets.log(`Preloading character: ${config.name}${hasEmbeddedProjectiles ? ' (with embedded projectiles)' : ''}${hasEmbeddedWeapons ? ' (with embedded weapons)' : ''}`);
    const asset = await this.loader.loadCharacter(
      config.id,
      config.assetPath,
      config.animations,
      config.modelAxes ?? config.rotationOffset,
      config.submeshes,
      config.renderFixes,
      needsEntityExtraction // forceEntityExtraction when we have embedded projectiles or weapons
    );

    // Store in cache
    this.cache.set(config.id, asset);
    
    // IMPORTANT: Hide the cached asset meshes - they're templates, not for display!
    // They need to exist for cloning but shouldn't be visible
    asset.rootMesh.setEnabled(false);
    asset.meshes.forEach(mesh => mesh.setEnabled(false));
    
    // Also hide submesh meshes - these are CLONES of embedded entities created by loadSubmesh
    // They're NOT in asset.meshes (which only contains the original GLB meshes)
    // Without this, the template's weapon mesh would be visible floating in the scene!
    asset.submeshes.forEach(submesh => {
      if (submesh.rootMesh) {
        submesh.rootMesh.setEnabled(false);
        submesh.rootMesh.isVisible = false;
        // Also hide all child meshes (the actual geometry)
        submesh.rootMesh.getChildMeshes?.()?.forEach((child: AbstractMesh) => {
          child.setEnabled(false);
          child.isVisible = false;
        });
      }
      if (submesh.debugMesh) {
        submesh.debugMesh.setEnabled(false);
        submesh.debugMesh.isVisible = false;
      }
    });
    
    logger.assets.debug(`Cached character: ${config.name}`);
  }

  /**
   * Get a cloned instance of a cached character asset (throws if not preloaded)
   * Returns a new instance with cloned meshes/skeleton but shared animations
   */
  get(characterId: string): CharacterAsset {
    const cachedAsset = this.cache.get(characterId);
    if (!cachedAsset) {
      throw new Error(`Character '${characterId}' not found in cache. Did you preload it?`);
    }
    
    // Clone skeleton first (if exists)
    // CRITICAL: skeleton.clone() does NOT clone the linked TransformNodes!
    // We need to manually clone the TransformNodes and re-link them
    let clonedSkeleton: Skeleton | null = null;
    const transformNodeMap = new Map<string, TransformNode>(); // Maps original TN name to cloned TN
    
    if (cachedAsset.skeleton) {
      const originalSkeleton = cachedAsset.skeleton;
      clonedSkeleton = originalSkeleton.clone(`${originalSkeleton.name}_instance`);
      
      // Clone the TransformNodes for each bone and re-link them
      // This is crucial for independent animation playback!
      for (let i = 0; i < clonedSkeleton.bones.length; i++) {
        const clonedBone = clonedSkeleton.bones[i];
        const originalBone = originalSkeleton.bones[i];
        const originalTN = originalBone.getTransformNode();
        
        if (originalTN) {
          // Clone the TransformNode
          const clonedTN = originalTN.clone(`${originalTN.name}_instance_${Date.now()}`, null, true);
          if (clonedTN) {
            // Link the cloned bone to the cloned TransformNode
            clonedBone.linkTransformNode(clonedTN as TransformNode);
            transformNodeMap.set(originalTN.name, clonedTN as TransformNode);
            // Parenting will be fixed after all TNs are cloned
          }
        }
      }
      
      // Fix TransformNode hierarchy (parent-child relationships)
      for (let i = 0; i < originalSkeleton.bones.length; i++) {
        const originalBone = originalSkeleton.bones[i];
        const originalTN = originalBone.getTransformNode();
        
        if (originalTN && originalTN.parent instanceof TransformNode) {
          const clonedTN = transformNodeMap.get(originalTN.name);
          const clonedParentTN = transformNodeMap.get(originalTN.parent.name);
          
          if (clonedTN && clonedParentTN) {
            clonedTN.parent = clonedParentTN;
          }
        }
      }
      
      logger.assets.debug(`Skeleton cloned with ${transformNodeMap.size} independent TransformNodes`);
    }
    
    // Clone root mesh only (not children - we clone them manually with proper control)
    // IMPORTANT: doNotCloneChildren=true so we can control visibility of each child mesh
    const clonedRoot = cachedAsset.rootMesh.clone(`${cachedAsset.rootMesh.name}_instance`, null, true);
    if (!clonedRoot) {
      throw new Error(`Failed to clone root mesh for ${characterId}`);
    }
    
    // Enable the cloned root (original was disabled for caching)
    clonedRoot.setEnabled(true);

    // IMPORTANT: In cached cloning, Babylon's skeleton clone does not automatically
    // re-parent linked bone TransformNodes under the character's mesh hierarchy.
    // If left unparented, any attachments/debug nodes parented to bone TNs won't
    // inherit the visual root transforms (characterVisualRoot / rootMesh rotation/scale).
    // Parent any root-level bone TransformNodes under the cloned root mesh.
    for (const tn of transformNodeMap.values()) {
      if (!tn.parent) {
        tn.parent = clonedRoot;
      }
    }
    
    // IMPORTANT: Copy orientation from original (includes modelAxes/rotationOffset applied during load)
    if (cachedAsset.rootMesh.rotationQuaternion) {
      clonedRoot.rotationQuaternion = cachedAsset.rootMesh.rotationQuaternion.clone();
      clonedRoot.rotation.set(0, 0, 0);
    } else {
      clonedRoot.rotationQuaternion = null;
      clonedRoot.rotation.copyFrom(cachedAsset.rootMesh.rotation);
    }
    
    // Build a set of meshes that belong to embedded projectile/weapon entities
    // These meshes are SKIPPED during cloning - they would create orphaned duplicates!
    // - Weapon meshes: The submesh system clones these and attaches to bones
    // - Projectile meshes: MeshFactory.createProjectileMesh clones from templates on-demand
    const embeddedEntityMeshes = new Set<string>();
    if (cachedAsset.embeddedEntities) {
      for (const entity of cachedAsset.embeddedEntities.entities.values()) {
        if (entity.type === 'projectile' || entity.type === 'weapon') {
          for (const mesh of entity.meshes) {
            embeddedEntityMeshes.add(mesh.name);
          }
        }
      }
    }
    
    // Clone each mesh from the cached asset and parent to the cloned root
    // Also track original->cloned mapping for embedded entity remapping
    // IMPORTANT: Skip embedded entity meshes entirely - they're cloned by the submesh system
    // Cloning them here would create orphaned duplicates floating in the scene
    const clonedMeshes: any[] = [];
    const meshCloneMap = new Map<AbstractMesh, AbstractMesh>(); // original -> cloned
    
    for (const originalMesh of cachedAsset.meshes) {
      // Skip embedded entity template meshes - submesh cloning handles these
      if (embeddedEntityMeshes.has(originalMesh.name)) {
        logger.assets.debug(`Skipping embedded entity mesh '${originalMesh.name}' - handled by submesh system`);
        continue;
      }
      
      if (originalMesh === cachedAsset.rootMesh) {
        // Root mesh already cloned
        clonedMeshes.push(clonedRoot);
        meshCloneMap.set(originalMesh, clonedRoot as any);
      } else {
        // Clone child mesh with its geometry and parent it to cloned root
        const clonedMesh = originalMesh.clone(`${originalMesh.name}_instance`, clonedRoot, false);
        if (clonedMesh) {
          clonedMesh.setEnabled(true);
          clonedMesh.isVisible = true;
          // Enable shadow receiving for character meshes
          clonedMesh.receiveShadows = true;
          clonedMeshes.push(clonedMesh);
          meshCloneMap.set(originalMesh, clonedMesh);
        }
      }
    }
    
    // Link cloned skeleton to cloned meshes
    if (clonedSkeleton) {
      clonedMeshes.forEach(mesh => {
        if (mesh.skeleton) {
          mesh.skeleton = clonedSkeleton;
        }
      });
    }
    
    // Clone submeshes
    const clonedSubmeshes = new Map<string, any>();
    cachedAsset.submeshes.forEach((submesh, key) => {
      logger.assets.debug(`Processing submesh '${key}'. attachBone: '${submesh.attachBone}'`);
      // IMPORTANT: doNotCloneChildren must be FALSE to clone child meshes (e.g., node0 with actual geometry)
      const clonedSubmeshRoot = submesh.rootMesh.clone(`${submesh.rootMesh.name}_instance`, null, false);
      
      if (!clonedSubmeshRoot) {
        logger.assets.error(`Failed to clone submesh root for ${submesh.id}`);
        return;
      }

      // Ensure cloned weapon and all child meshes are enabled and visible
      clonedSubmeshRoot.setEnabled(true);
      clonedSubmeshRoot.isVisible = true;
      clonedSubmeshRoot.getChildMeshes().forEach(m => {
        m.setEnabled(true);
        m.isVisible = true;
      });

      let debugMesh = submesh.debugMesh; // Default to shared if we can't clone properly

      // Re-attach to cloned skeleton if applicable
      if (submesh.attachBone && clonedSkeleton) {
        const boneName = submesh.attachBone;
        const bone = clonedSkeleton.bones.find(b => b.name === boneName);
        
        if (bone) {
          // Create new anchor for this instance and attach to bone using attachToBone()
          // attachToBone is the ONLY way to properly follow animated bones
          const anchor = new TransformNode(`${submesh.id}_anchor_instance`, clonedRoot.getScene());
          anchor.attachToBone(bone, clonedRoot);
          
          // Normalize bone scaling on anchor
          const boneMatrix = bone.getAbsoluteTransform();
          const boneScale = new Vector3();
          boneMatrix.decompose(boneScale, undefined, undefined);
          if (boneScale.lengthSquared() > 0.0001) {
            const normalizationFactor = new Vector3(1/boneScale.x, 1/boneScale.y, 1/boneScale.z);
            anchor.scaling.copyFrom(normalizationFactor);
          }

          // Parent the cloned submesh to the new anchor
          clonedSubmeshRoot!.parent = anchor;

            // Create new debug sphere for this instance
            const debugSphere = MeshBuilder.CreateSphere(`weapon_mount_debug`, { diameter: 0.5 }, clonedRoot.getScene());
            debugSphere.id = `weapon_mount_debug_${submesh.id}_instance`;
            debugSphere.renderingGroupId = 1;
            const debugMat = new StandardMaterial(`weapon_mount_debug_mat_instance`, clonedRoot.getScene());
            debugMat.diffuseColor = new Color3(1, 1, 0);
            debugMat.emissiveColor = new Color3(1, 1, 0);
            debugMat.disableLighting = true;
            debugMat.wireframe = false;
            debugSphere.material = debugMat;
            debugSphere.isPickable = false;
            debugSphere.isVisible = true; // Visible by default
            debugSphere.layerMask = 0x20000000; // Debug layer (bit 29)
          
            debugSphere.parent = anchor;
            debugSphere.position = Vector3.Zero();
            debugSphere.rotation = Vector3.Zero();
            debugSphere.scaling = Vector3.One();

            debugMesh = debugSphere;
          
            logger.assets.debug(`Attached submesh '${submesh.id}' to bone '${boneName}'`);
        } else {
          logger.assets.warn(`Could not find bone '${boneName}' in cloned skeleton for submesh '${submesh.id}'`);
        }
      }

      // Clone submesh animations to avoid shared playback state
      const clonedSubmeshAnimations = new Map<string, AnimationGroup>();
      submesh.animations.forEach((animGroup: AnimationGroup, animName: string) => {
        const clonedAnim = this.cloneAnimationGroup(animGroup, clonedSkeleton);
        clonedSubmeshAnimations.set(animName, clonedAnim);
      });
      
      clonedSubmeshes.set(key, {
        id: submesh.id,
        rootMesh: clonedSubmeshRoot!,
        animations: clonedSubmeshAnimations,
        animationMap: submesh.animationMap,
        debugMesh: debugMesh,
        attachBone: submesh.attachBone // Preserve for re-attachment in Character.loadFromAsset()
      });
    });
    
    // Clone animations to give each character instance independent playback state
    const clonedAnimations = new Map<string, AnimationGroup>();
    cachedAsset.animations.forEach((animGroup, animName) => {
      const clonedAnim = this.cloneAnimationGroup(animGroup, clonedSkeleton);
      clonedAnimations.set(animName, clonedAnim);
    });
    
    // Clone phaseMap with cloned animation groups
    const clonedPhaseMap = new Map<AnimationPhase, AnimationGroup>();
    cachedAsset.phaseMap.forEach((animGroup, phase) => {
      // Find the cloned animation by matching original group
      // First check if it's in the animations map (by finding the key)
      let clonedAnim: AnimationGroup | undefined;
      
      // Find the animation name that maps to this group
      cachedAsset.animations.forEach((originalAnim, animName) => {
        if (originalAnim === animGroup) {
          clonedAnim = clonedAnimations.get(animName);
        }
      });
      
      // If not found, clone it directly (for external animations)
      if (!clonedAnim) {
        clonedAnim = this.cloneAnimationGroup(animGroup, clonedSkeleton);
        // Also add to clonedAnimations for consistency
        clonedAnimations.set(`${phase}_cloned`, clonedAnim);
      }
      
      clonedPhaseMap.set(phase, clonedAnim);
    });
    
    // Reference embedded entities for projectile/weapon mesh cloning
    // NOTE: We intentionally DON'T clone embedded entity meshes here!
    // - Weapon meshes: Cloned by submesh system and attached to bones
    // - Projectile meshes: Cloned on-demand by MeshFactory.createProjectileMesh
    // The entities still need to be referenced so projectile spawning can find them
    let clonedEmbeddedEntities: ExtractionResult | undefined;
    if (cachedAsset.embeddedEntities) {
      const clonedEntitiesMap = new Map<string, ExtractedEntity>();
      
      for (const [entityId, originalEntity] of cachedAsset.embeddedEntities.entities) {
        // For embedded projectile/weapon entities, use the ORIGINAL template meshes
        // They're kept disabled in the scene but are valid sources for cloning
        // No need to remap - createProjectileMesh clones from these on-demand
        const clonedEntity: ExtractedEntity = {
          type: originalEntity.type,
          id: originalEntity.id,
          behaviors: originalEntity.behaviors,
          root: originalEntity.root, // Use original root (template)
          meshes: originalEntity.meshes, // Use original meshes (templates for cloning)
          skeleton: clonedSkeleton, // Use cloned skeleton for animation targeting
          animations: originalEntity.animations, // Animations are shared
          extras: originalEntity.extras,
        };
        
        clonedEntitiesMap.set(entityId, clonedEntity);
        logger.assets.debug(`Referenced embedded entity '${entityId}' with ${originalEntity.meshes.length} template meshes`);
      }
      
      clonedEmbeddedEntities = {
        entities: clonedEntitiesMap,
        container: cachedAsset.embeddedEntities.container, // Container is shared
      };
    }
    
    return {
      rootMesh: clonedRoot,
      meshes: clonedMeshes,
      skeleton: clonedSkeleton,
      animations: clonedAnimations,
      phaseMap: clonedPhaseMap,
      submeshes: clonedSubmeshes,
      embeddedEntities: clonedEmbeddedEntities
    };
  }
  
  /**
   * Clone an AnimationGroup and retarget it to a new skeleton
   * This ensures each character instance has independent animation playback state
   */
  private cloneAnimationGroup(source: AnimationGroup, targetSkeleton: Skeleton | null): AnimationGroup {
    const cloned = new AnimationGroup(
      `${source.name}_instance_${Date.now()}`,
      source.getScene()
    );
    
    let retargetedCount = 0;
    let fallbackCount = 0;
    
    for (const targetedAnim of source.targetedAnimations) {
      const clonedAnim = targetedAnim.animation.clone();
      
      // If we have a target skeleton, try to retarget bone animations
      if (targetSkeleton && targetedAnim.target?.name) {
        const targetName = targetedAnim.target.name;
        
        // Try to find matching bone in target skeleton by name
        const bone = targetSkeleton.bones.find(b => b.name === targetName);
        if (bone) {
          // Use TransformNode if available (GLTF models)
          const transformNode = bone.getTransformNode();
          if (transformNode) {
            cloned.addTargetedAnimation(clonedAnim, transformNode);
            retargetedCount++;
          } else {
            cloned.addTargetedAnimation(clonedAnim, bone);
            retargetedCount++;
          }
          continue;
        }
        
        // Also check if target is a TransformNode linked to a bone (by TransformNode name)
        const boneByTransform = targetSkeleton.bones.find(b => {
          const tn = b.getTransformNode();
          return tn && tn.name === targetName;
        });
        if (boneByTransform) {
          const transformNode = boneByTransform.getTransformNode();
          if (transformNode) {
            cloned.addTargetedAnimation(clonedAnim, transformNode);
            retargetedCount++;
            continue;
          }
        }
      }
      
      // Fallback: use original target (for non-bone animations)
      // WARNING: This can cause animation linking if the target is a bone from the original skeleton!
      cloned.addTargetedAnimation(clonedAnim, targetedAnim.target);
      fallbackCount++;
    }
    
    // Log retargeting stats only if there were issues
    if (fallbackCount > 0) {
      logger.assets.warn(`Animation clone '${cloned.name}': ${fallbackCount}/${source.targetedAnimations.length} targets used fallback (may cause linked animations!)`);
    }
    
    // Copy timing
    cloned.normalize(source.from, source.to);
    
    // Copy metadata (contains animation config like naturalSpeed, blendConfig, etc.)
    if (source.metadata) {
      cloned.metadata = { ...source.metadata };
    }
    
    return cloned;
  }

  /**
   * Check if a character is cached
   */
  has(characterId: string): boolean {
    return this.cache.has(characterId);
  }

  /**
   * Clear the entire cache
   */
  clear(): void {
    for (const asset of this.cache.values()) {
      asset.rootMesh.dispose();
      asset.meshes.forEach(mesh => mesh.dispose());
    }
    this.cache.clear();
  }
}
